<template>
  <div class="page-container">
    <main :class="['main-content', { nested: isMemberCenterContext }]">
      <div class="page-inner-box">
        <div class="my-orders-container">
          <div class="content-main">
            <!-- 订单状态标签页 -->
            <div class="order-tabs">
              <div 
                v-for="tab in tabs" 
                :key="tab.name"
                class="tab-item"
                :class="{ active: activeTab === tab.name }"
                @click="handleTabClick(tab)"
              >
                {{ tab.label }}
              </div>
            </div>
            
            <div class="divider"></div>

            <!-- 订单列表 -->
            <div v-loading="ordersLoading" class="orders-list">
              <!-- 订单记录 -->
              <div v-for="(order, index) in orders" :key="order.orderNo ?? order.id ?? index" class="order-card">
                <div class="order-header">
                  <div class="order-date">订单日期：{{ order.orderDate }}</div>
                  <div class="order-status" :class="{ 'status-danger': order.statusText === '待付款' && order.remainSeconds > 0 && order.remainSeconds <= 300 }">
                    {{ order.statusText }}
                    <span v-if="order.statusText === '待付款' && order.remainSeconds > 0" class="countdown-text" :class="{ 'countdown-danger': order.remainSeconds <= 300 }">
                      （{{ formatCountdown(order.remainSeconds) }}后自动关闭）
                    </span>
                  </div>
                </div>
                
                <div class="order-content">
                  <div class="product-info">
                    <div
                      class="product-image"
                      :style="order.productImage ? { backgroundImage: `url(${order.productImage})` } : undefined"
                    ></div>
                    <div class="product-details">
                      <div class="product-title">{{ order.productTitle }}</div>
                      <div class="product-spec">{{ order.productSpec }}</div>
                      <div class="product-price">¥{{ order.productPrice }}</div>
                    </div>
                    <div class="product-quantity">× {{ order.quantity }}</div>
                  </div>
                </div>

                <div class="order-footer">
                  <div class="divider-dashed"></div>
                  <div class="order-summary">
                    <div class="summary-text">
                      <span v-if="order.couponAmount !== '0.00'" class="coupon-info">优惠券抵扣：-¥{{ order.couponAmount }}</span>
                      <span v-if="order.coinAmount !== '0.00'" class="coin-info">  金币抵扣：-¥{{ order.coinAmount }}</span>
                      <br/>
                      实付金额 <span class="total-price">¥{{ order.cashAmount }}</span>
                    </div>
                    <div class="order-actions">
                      <el-button v-if="order.statusText === '待付款'" class="detail-btn" type="primary" size="small" style="margin-right: 10px;" @click="goToPayment(order)">去支付</el-button>
                      <el-button v-if="order.statusText === '已完成' || order.statusText === '已退款'" class="detail-btn" type="danger" size="small" style="margin-right: 10px;" @click="handleHideOrder(order)">删除</el-button>
                      <el-button v-if="order.statusText === '已过期' || order.statusText === '订单关闭'" class="detail-btn" type="danger" size="small" style="margin-right: 10px;" @click="handleDeleteOrder(order)">删除订单</el-button>
                      <el-button class="detail-btn" @click="viewOrderDetail(order)">查看详情</el-button>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- 无订单提示 -->
              <div v-if="!ordersLoading && orders.length === 0" class="no-orders">
                <el-empty description="暂无订单" />
              </div>
            </div>
            
            <!-- 分页 -->
            <div class="pagination">
              <el-pagination
                layout="prev, pager, next"
                :total="ordersTotal"
                :page-size="ordersQuery.pageSize"
                :current-page="ordersQuery.pageNum"
                prev-text="<"
                next-text=">"
                @current-change="handleCurrentChange"
              />
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { getMyOrders, deleteMallOrder, hideOrder } from '@/api/orderApi'
import { mapGetters } from 'vuex'

export default {
  name: 'MyOrders',
  created() {
    this.loadOrders()
    this.startCountdownTimer()
  },
  beforeDestroy() {
    this.stopCountdownTimer()
  },
  computed: {
    ...mapGetters('auth', ['userInfo']),
    isMemberCenterContext() {
      return this.$route.path.startsWith('/member-center')
    }
  },
  data() {
    return {
      activeTab: 'all',
      tabs: [
        { name: 'all', label: '全部订单' },
        { name: 'course', label: '课程订单' },
        { name: 'paper', label: '题库订单' },
        { name: 'mall', label: '其他订单' }
      ],
      ordersLoading: false,
      ordersTotal: 0,
      ordersQuery: {
        orderCategory: undefined,
        pageNum: 1,
        pageSize: 10
      },
      ordersLoadSeq: 0,
      orders: []
    }
  },
  methods: {
    handleDeleteOrder(order) {
      this.$confirm('确定要删除该订单吗？删除后将无法恢复', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
           if (order.raw && order.raw.orderStatus !== undefined) { // 商城订单
             const category = (order.raw && order.raw.orderCategory) || this.ordersQuery.orderCategory
           await deleteMallOrder(order.id, category)
             this.$message.success('删除成功')
             this.loadOrders()
          } else {
             this.$message.warning('暂不支持删除此类型订单')
          }
        } catch (error) {
          // 错误已在拦截器中提示，这里不再重复提示
          void error
        }
      }).catch(() => {})
    },
    handleHideOrder(order) {
      this.$confirm('确定要删除该订单吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          const category = order.raw?.orderCategory || this.ordersQuery.orderCategory || 'mall'
          await hideOrder(order.id, category)
          this.$message.success('删除成功')
          this.loadOrders()
        } catch (error) {
          void error
        }
      }).catch(() => {})
    },
    extractListAndTotal(data) {
      const list =
        (Array.isArray(data) && data) ||
        (data && Array.isArray(data.list) && data.list) ||
        (data && Array.isArray(data.records) && data.records) ||
        (data && Array.isArray(data.items) && data.items) ||
        []

      const total =
        (data && (data.total ?? data.count ?? data.totalCount)) ||
        (typeof data === 'object' && !Array.isArray(data) ? list.length : list.length)

      return { list, total: Number(total) || 0 }
    },
    normalizeOrder(item, index) {
      const id = (item && (item.id ?? item.orderId)) ?? index
      const orderNo = item && (item.orderNo ?? item.order_no)
      const orderDate = String((item && (item.createTime ?? item.payTime ?? item.updateTime)) || '')
      const orderType = item && (item.orderType ?? item.type)

      // 订单原价
      const amountRaw = item && (item.amount ?? item.payAmount)
      const amount = Number(amountRaw)
      const amountText = Number.isFinite(amount) ? amount.toFixed(2) : '—'
      
      // 实际支付金额（现金）
      const cashAmountRaw = item && item.cashAmount
      const cashAmount = Number(cashAmountRaw)
      const cashAmountText = Number.isFinite(cashAmount) ? cashAmount.toFixed(2) : '—'
      
      // 优惠券抵扣金额
      const couponAmountRaw = item && item.couponAmount
      const couponAmount = Number(couponAmountRaw)
      const couponAmountText = Number.isFinite(couponAmount) ? couponAmount.toFixed(2) : '0.00'
      
      // 金币抵扣金额
      const coinAmountRaw = item && item.coinAmount
      const coinAmount = Number(coinAmountRaw)
      const coinAmountText = Number.isFinite(coinAmount) ? coinAmount.toFixed(2) : '0.00'

      const title =
        (item && (item.productName ?? item.courseName ?? item.paperName ?? item.productTitle ?? item.title ?? item.name)) ||
        (orderNo ? `订单号：${orderNo}` : `订单：${id}`)

      const productImage =
        (item && (item.productImage ?? item.courseImage ?? item.image ?? item.cover ?? item.coverImage)) ||
        require('@/assets/image/study_placeholder.png')

      const region = item && item.region
      const spec = region ? `地区：${region}` : ''

      const payStatus = item && (item.payStatus ?? item.pay_status ?? item.orderStatus)
      const remainSeconds = item && item.remainSeconds
      const isOverdue = item && item.isOverdue
      let isExpired = false
      if (payStatus === 0 || payStatus === '0') {
        if (isOverdue || (remainSeconds != null && remainSeconds <= 0)) {
          isExpired = true
        }
      }
      const statusText =
        payStatus === 0 || payStatus === '0'
          ? (isExpired ? '已过期' : '待付款')
          : payStatus === 1 || payStatus === '1'
            ? '已完成'
            : payStatus === 2 || payStatus === '2'
              ? '已退款'
              : payStatus === 3 || payStatus === '3'
                ? '已完成'
                : payStatus === 4 || payStatus === '4'
                  ? '已退款'
                  : payStatus === 5 || payStatus === '5'
                    ? '订单关闭'
                    : payStatus === 6 || payStatus === '6'
                      ? '退款中'
                      : '订单处理中'

      return {
        id,
        orderNo,
        orderType,
        orderDate,
        statusText,
        remainSeconds: remainSeconds != null ? Math.max(0, Number(remainSeconds)) : null,
        productTitle: String(title || ''),
        productSpec: String(spec || ''),
        productImage: String(productImage || ''),
        productPrice: amountText,
        quantity: 1,
        totalItems: 1,
        totalPrice: amountText,
        cashAmount: cashAmountText,
        couponAmount: couponAmountText,
        coinAmount: coinAmountText,
        shippingFee: '0.00',
        raw: item
      }
    },
    async loadOrders() {
      const seq = ++this.ordersLoadSeq
      this.ordersLoading = true
      try {
        const res = await getMyOrders(this.ordersQuery)
        if (seq !== this.ordersLoadSeq) return
        const data = res && res.data
        const { list, total } = this.extractListAndTotal(data)
        this.orders = list.map((item, index) => this.normalizeOrder(item, index))
        this.ordersTotal = total
      } catch (err) {
        if (seq !== this.ordersLoadSeq) return
        this.orders = []
        this.ordersTotal = 0
        this.$message.error((err && err.message) || '获取我的订单失败')
      } finally {
        if (seq === this.ordersLoadSeq) {
          this.ordersLoading = false
        }
      }
    },
    createNewOrder() {
      this.$message({
        message: '创建订单功能待实现',
        type: 'info'
      })
    },
    viewOrderDetail(orderOrId) {
      const order = orderOrId && typeof orderOrId === 'object' ? orderOrId : null
      const orderId = order ? order.id : orderOrId
      const cacheKey = `snails_order_detail_${orderId}`
      if (orderId != null && order) {
        try {
          const payload = order.raw != null ? order.raw : order
          localStorage.setItem(cacheKey, JSON.stringify(payload))
        } catch (e) {
          void e
        }
      }
      // 根据当前上下文跳转到不同的订单详情路由
      if (this.isMemberCenterContext) {
        this.$router.push(`/member-center/order/${orderId}`)
      } else {
        this.$router.push(`/order/${orderId}`)
      }
    },
    goToPayment(order) {
      if (!order || !order.id) return;
      const raw = order.raw || {}
      const orderNo = order.orderNo
      const payType = raw.payType ?? raw.paymentType ?? raw.payment_type
      const totalAmount = raw.amount ?? raw.cashAmount ?? raw.payAmount

      if (raw.productId) {
        this.$router.push({
          path: `/product-payment/${raw.productId}`,
          query: {
            orderNo,
            payType,
            totalAmount,
            title: order.productTitle,
            image: order.productImage
          }
        })
        return
      }

      const courseId = raw.courseId || 0
      this.$router.push({
        path: `/payment/${courseId}`,
        query: {
          orderNo,
          bizType: 'course',
          payType,
          totalAmount,
          title: order.productTitle,
          image: order.productImage
        }
      })
    },
    handleCurrentChange(val) {
      this.ordersQuery.pageNum = val
      this.orders = []
      this.loadOrders()
    },
    handleTabClick(tab) {
      this.activeTab = tab.name
      this.ordersQuery.pageNum = 1
      this.ordersQuery.orderCategory = tab.name === 'all' ? undefined : tab.name
      this.orders = []
      this.ordersTotal = 0
      this.loadOrders()
    },
    startCountdownTimer() {
      this.stopCountdownTimer()
      this._countdownTimer = setInterval(() => {
        let hasActive = false
        this.orders.forEach(order => {
          if (order.statusText === '待付款' && order.remainSeconds > 0) {
            order.remainSeconds--
            hasActive = true
            if (order.remainSeconds <= 0) {
              order.statusText = '已过期'
            }
          }
        })
        if (!hasActive) {
          this.stopCountdownTimer()
        }
      }, 1000)
    },
    stopCountdownTimer() {
      if (this._countdownTimer) {
        clearInterval(this._countdownTimer)
        this._countdownTimer = null
      }
    },
    formatCountdown(seconds) {
      if (seconds == null || seconds <= 0) return '0分0秒'
      const m = Math.floor(seconds / 60)
      const s = seconds % 60
      return `${m}分${s}秒`
    }
  }
}
</script>

<style scoped lang="scss">
.page-container { min-height: 100vh; display: flex; flex-direction: column; }
.main-content { flex: 1; padding-top: 20px; }
.main-content.nested { margin-top: 0; }
.main-content.nested .page-inner-box { width: 100%; margin: 0; }

.my-orders-container {
  display: flex;
  gap: 24px;
}

.content-main {
  flex: 1;
  background-color: #fff;
  min-height: 500px;
}

/* 标签页样式 */
.order-tabs {
  display: flex;
  margin-bottom: 15px;
  padding-top: 10px;
}

.tab-item {
  margin-right: 30px;
  font-size: 16px;
  color: #666;
  cursor: pointer;
  padding-bottom: 10px;
  
  &:hover {
    color: #3E2CCD;
  }
  
  &.active {
    color: #3E2CCD;
    font-weight: 600;
  }
}

.divider {
  height: 1px;
  background-color: #eee;
  margin-bottom: 20px;
}

/* 订单列表 */
.orders-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 10px;
  background-color: #f5f5f5;
}

/* 订单卡片 */
.order-card {
  background-color: #fff;
  padding: 20px;
}

.order-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.order-date {
  font-size: 14px;
  color: #999;
}

.order-status {
  font-size: 16px;
  color: #3E2CCD;
  font-weight: 500;
}

.order-content {
  margin-bottom: 20px;
}

/* 商品信息 */
.product-info {
  display: flex;
  position: relative;
}

.product-image {
  width: 120px;
  height: 90px;
  background-color: #f5f7fa;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  border-radius: 4px;
  margin-right: 15px;
}

.product-details {
  flex: 1;
  padding-right: 50px;
}

.product-title {
  font-size: 14px;
  color: #333;
  margin-bottom: 10px;
  line-height: 1.4;
}

.product-spec {
  font-size: 14px;
  color: #999;
  margin-bottom: 10px;
}

.product-price {
  font-size: 16px;
  color: #FF4433;
  font-weight: 500;
}

.product-quantity {
  position: absolute;
  right: 0;
  top: 40px;
  font-size: 14px;
  color: #666;
}

.order-footer {
  margin-top: 10px;
}

.divider-dashed {
  height: 1px;
  border-top: 1px dashed #e0e0e0;
  margin-bottom: 15px;
}

.order-summary {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.summary-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 0;
  line-height: 1.8;
}

.coupon-info, .coin-info {
  font-size: 13px;
  color: #ff6b6b;
  margin-right: 10px;
}

.coin-info {
  color: #ffa940;
}

.total-price {
  font-size: 16px;
  color: #FF4433;
  font-weight: 500;
  margin: 0 5px;
}

.shipping-fee {
  font-size: 14px;
  color: #666;
}

.order-actions {
  display: flex;
  justify-content: flex-end;
  flex-shrink: 0;
}

.detail-btn {
  background-color: #3E2CCD;
  border-color: #3E2CCD;
  color: #fff;
  padding: 8px 25px;
  border-radius: 4px;
  font-size: 14px;
  
  &:hover {
    background-color: #3325ad;
    border-color: #3325ad;
  }
}

/* 无订单提示 */
.no-orders {
  text-align: center;
  padding: 50px 0;
}

/* 分页 */
.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
  padding-bottom: 20px;
}

::v-deep .el-pagination {
  .btn-prev, .btn-next {
    background-color: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    padding: 0 10px;
    height: 32px;
    line-height: 32px;
    min-width: 32px;
  }
  
  .el-pager li {
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    margin: 0 5px;
    color: #606266;
    height: 32px;
    line-height: 32px;
    min-width: 32px;
    
    &.active {
      background-color: #3E2CCD;
      border-color: #3E2CCD;
      color: #fff;
    }
  }
}

.countdown-text {
  font-size: 12px;
  margin-left: 4px;
}

.countdown-danger {
  color: #f56c6c !important;
  font-weight: bold;
}

.status-danger {
  color: #f56c6c;
}
</style>
